import { useState, useEffect } from 'react';
import AdminCRUDLayout from '../../components/admin/AdminCRUDLayout';
import UnifiedDataTable from '../../components/admin/UnifiedDataTable';
import StandardModal from '../../components/admin/StandardModal';
import StandardForm from '../../components/admin/StandardForm';
import venueService from '../../services/venueService';
import './VenueManagementUnified.css';

/**
 * Gestión de sedes con el sistema unificado
 * Implementación completa usando los componentes base
 */
const VenueManagementUnified = () => {
  const [venues, setVenues] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Estados para modales
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  // Estado para el elemento actual
  const [currentItem, setCurrentItem] = useState(null);

  // Estado para formularios
  const [formData, setFormData] = useState({});
  const [formErrors, setFormErrors] = useState({});
  const [formLoading, setFormLoading] = useState(false);

  // Estado para campos (instalaciones físicas de la sede)
  const [campos, setCampos] = useState([]);
  const [camposLoading, setCamposLoading] = useState(false);
  const [campoFormMode, setCampoFormMode] = useState(null); // null | 'add' | 'edit'
  const [editingCampo, setEditingCampo] = useState(null);
  const [campoFormData, setCampoFormData] = useState({});
  const [campoFormLoading, setCampoFormLoading] = useState(false);
  const [campoFormError, setCampoFormError] = useState('');

  // Cargar sedes
  const loadVenues = async () => {
    console.log('Iniciando carga de sedes...');
    setLoading(true);
    setError(null);

    try {
      console.log('Llamando a venueService.getVenues()...');
      const response = await venueService.getVenues();
      console.log('Respuesta del servicio de sedes:', response);

      // Procesar datos de PostgreSQL
      const data = response.data || response;
      console.log('Datos extraídos:', data);

      const processedData = Array.isArray(data) ? data.map(sede => ({
        id: sede.id,
        name: sede.nombre,
        description: sede.ubicacion || '',
        address: {
          street: sede.direccion || '',
          city: sede.poblacion || '',
          postalCode: '',
          country: 'España'
        },
        addressText: `${sede.direccion || ''}, ${sede.poblacion || ''}`.trim().replace(/^,\s*/, ''),
        contactInfo: {
          name: sede.contacto || '',
          phone: '',
          email: ''
        },
        coordinates: {
          lat: sede.latitud,
          lng: sede.longitud
        },
        facilities: [],
        facilitiesCount: (sede.num_campos_f8 || 0) + (sede.num_campos_f11 || 0),
        fieldsF8: sede.num_campos_f8 || 0,
        fieldsF11: sede.num_campos_f11 || 0,
        active: sede.activo,
        status: sede.activo ? 'active' : 'inactive'
      })) : [];

      console.log('Datos procesados:', processedData);
      setVenues(processedData);
    } catch (err) {
      console.error('Error cargando sedes:', err);
      console.error('Detalles del error:', err.response?.data || err.message);
      setError(err.message || 'Error al cargar las sedes');
    } finally {
      setLoading(false);
    }
  };

  // Cargar datos al montar
  useEffect(() => {
    loadVenues();
  }, []);

  // Funciones para manejar modales
  const openCreateModal = () => {
    setFormData({
      name: '',
      direccion: '',
      poblacion: '',
      ubicacion: '',
      fieldsF8: 0,
      fieldsF11: 0,
      contacto: '',
      latitud: null,
      longitud: null,
      active: true
    });
    setFormErrors({});
    setCurrentItem(null);
    setShowCreateModal(true);
  };

  const openEditModal = (item) => {
    console.log('openEditModal - item recibido:', item);

    setCurrentItem(item);
    const newFormData = {
      name: item.name || '',
      direccion: item.address?.street || '',
      poblacion: item.address?.city || '',
      ubicacion: item.description || '',
      fieldsF8: item.fieldsF8 || 0,
      fieldsF11: item.fieldsF11 || 0,
      contacto: item.contactInfo?.name || '',
      latitud: item.coordinates?.lat || null,
      longitud: item.coordinates?.lng || null,
      active: item.active !== undefined ? item.active : true
    };

    console.log('openEditModal - formData creado:', newFormData);

    setFormData(newFormData);
    setFormErrors({});
    setShowEditModal(true);
  };

  const openViewModal = (item) => {
    setCurrentItem(item);
    setShowViewModal(true);
    loadCampos(item.id);
  };

  const openDeleteModal = (item) => {
    setCurrentItem(item);
    setShowDeleteModal(true);
  };

  const closeAllModals = () => {
    setShowCreateModal(false);
    setShowEditModal(false);
    setShowViewModal(false);
    setShowDeleteModal(false);
    setCurrentItem(null);
    setFormData({});
    setFormErrors({});
    // Resetear estado de campos
    setCampos([]);
    setCampoFormMode(null);
    setEditingCampo(null);
    setCampoFormData({});
    setCampoFormError('');
  };

  // Función para validar datos
  const validateData = (data) => {
    const errors = {};

    if (!data.name?.trim()) {
      errors.name = 'El nombre es obligatorio';
    }

    if (!data.direccion?.trim()) {
      errors.direccion = 'La dirección es obligatoria';
    }

    if (!data.poblacion?.trim()) {
      errors.poblacion = 'La población es obligatoria';
    }

    if (data.capacity && (isNaN(data.capacity) || data.capacity < 0)) {
      errors.capacity = 'La capacidad debe ser un número válido mayor o igual a 0';
    }

    if (data.lat && (isNaN(data.lat) || data.lat < -90 || data.lat > 90)) {
      errors.lat = 'La latitud debe estar entre -90 y 90';
    }

    if (data.lng && (isNaN(data.lng) || data.lng < -180 || data.lng > 180)) {
      errors.lng = 'La longitud debe estar entre -180 y 180';
    }

    return errors;
  };

  // Función para crear sede
  const handleCreate = async (e) => {
    e.preventDefault();
    console.log('handleCreate ejecutado con datos:', formData);
    setFormLoading(true);
    setFormErrors({});

    const errors = validateData(formData);
    if (Object.keys(errors).length > 0) {
      console.log('Errores de validación:', errors);
      setFormErrors(errors);
      setFormLoading(false);
      return;
    }

    try {
      await venueService.createVenue(formData);
      closeAllModals();
      loadVenues();
    } catch (err) {
      console.error('Error creando sede:', err);
      console.error('Detalles del error:', err.response?.data || err.message);
      setError(err.message || 'Error al crear la sede');
    } finally {
      setFormLoading(false);
    }
  };

  // Función para actualizar sede
  const handleUpdate = async (e) => {
    e.preventDefault();
    console.log('handleUpdate ejecutado con datos:', formData);
    console.log('currentItem:', currentItem);
    setFormLoading(true);
    setFormErrors({});

    const errors = validateData(formData);
    if (Object.keys(errors).length > 0) {
      console.log('Errores de validación:', errors);
      setFormErrors(errors);
      setFormLoading(false);
      return;
    }

    try {
      const id = currentItem?.id || currentItem?._id;
      await venueService.updateVenue(id, formData);
      closeAllModals();
      loadVenues();
    } catch (err) {
      console.error('Error actualizando sede:', err);
      console.error('Detalles del error:', err.response?.data || err.message);
      setError(err.message || 'Error al actualizar la sede');
    } finally {
      setFormLoading(false);
    }
  };

  // Función para eliminar sede
  const handleDelete = async () => {
    setFormLoading(true);

    try {
      const id = currentItem?.id || currentItem?._id;
      console.log('Eliminando sede con ID:', id);
      await venueService.deleteVenue(id);
      closeAllModals();
      loadVenues();
      console.log('Sede eliminada correctamente');
    } catch (err) {
      console.error('Error eliminando sede:', err);
      console.error('Detalles del error:', err.response?.data || err.message);
      setError(err.message || 'Error al eliminar la sede');
    } finally {
      setFormLoading(false);
    }
  };

  // Función para activar/desactivar sede
  const handleToggleActive = async (item) => {
    console.log('handleToggleActive ejecutado con item:', item);
    try {
      const id = item?.id || item?._id;
      const nuevoEstado = !item.active;
      console.log('Cambiando estado de la sede ID:', id, 'activo:', nuevoEstado);

      await venueService.updateVenue(id, { active: nuevoEstado });

      loadVenues();
      console.log(`Sede ${nuevoEstado ? 'activada' : 'desactivada'} correctamente`);
    } catch (err) {
      console.error('Error cambiando estado de sede:', err);
      console.error('Detalles del error:', err.response?.data || err.message);
      setError(err.message || 'Error al cambiar el estado de la sede');
    }
  };

  // Función para manejar cambios en formulario
  const handleFormChange = (field, value) => {
    console.log('handleFormChange - campo:', field, 'valor:', value);

    if (field.includes('.')) {
      const [parent, child] = field.split('.');
      setFormData(prev => {
        const newData = {
          ...prev,
          [parent]: {
            ...prev[parent],
            [child]: value
          }
        };
        console.log('Nuevos datos del formulario (campo anidado):', newData);
        return newData;
      });
    } else {
      setFormData(prev => {
        const newData = {
          ...prev,
          [field]: value
        };
        console.log('Nuevos datos del formulario (campo simple):', newData);
        return newData;
      });
    }

    // Limpiar error del campo si existe
    if (formErrors[field]) {
      setFormErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  // ── Handlers de campos ─────────────────────────────────────────────────────

  const loadCampos = async (sedeId) => {
    setCamposLoading(true);
    try {
      const res = await venueService.getCamposBySede(sedeId, { activo: 'all' });
      setCampos(res.data || []);
    } catch (err) {
      console.error('Error cargando campos de sede:', err);
      setCampos([]);
    } finally {
      setCamposLoading(false);
    }
  };

  const openAddCampoForm = () => {
    setCampoFormData({ nombre: '', direccion: '', num_campos_f8: 0, num_campos_f11: 0 });
    setCampoFormError('');
    setEditingCampo(null);
    setCampoFormMode('add');
  };

  const openEditCampoForm = (campo) => {
    setCampoFormData({
      nombre: campo.nombre || '',
      direccion: campo.direccion || '',
      num_campos_f8: campo.num_campos_f8 || 0,
      num_campos_f11: campo.num_campos_f11 || 0,
    });
    setCampoFormError('');
    setEditingCampo(campo);
    setCampoFormMode('edit');
  };

  const cancelCampoForm = () => {
    setCampoFormMode(null);
    setEditingCampo(null);
    setCampoFormData({});
    setCampoFormError('');
  };

  const handleCampoSave = async () => {
    if (!campoFormData.nombre?.trim()) {
      setCampoFormError('El nombre del campo es obligatorio');
      return;
    }
    setCampoFormLoading(true);
    setCampoFormError('');
    try {
      if (campoFormMode === 'add') {
        await venueService.createCampo(currentItem.id, campoFormData);
      } else {
        await venueService.updateCampo(currentItem.id, editingCampo.id, campoFormData);
      }
      cancelCampoForm();
      loadCampos(currentItem.id);
    } catch (err) {
      setCampoFormError(err.message || 'Error al guardar el campo');
    } finally {
      setCampoFormLoading(false);
    }
  };

  const handleCampoToggleActivo = async (campo) => {
    try {
      await venueService.updateCampo(currentItem.id, campo.id, { activo: !campo.activo });
      loadCampos(currentItem.id);
    } catch (err) {
      console.error('Error cambiando estado del campo:', err);
    }
  };

  // ──────────────────────────────────────────────────────────────────────────

  const clearError = () => setError(null);

  // Configuración de columnas
  const columns = [
    {
      key: 'name',
      label: 'Nombre'
    },
    {
      key: 'addressText',
      label: 'Dirección',
      className: 'hide-on-mobile'
    },
    {
      key: 'fieldsF8',
      label: 'Campos F8',
      className: 'hide-on-mobile',
      render: (row) => row.fieldsF8 || 0
    },
    {
      key: 'fieldsF11',
      label: 'Campos F11',
      className: 'hide-on-mobile',
      render: (row) => row.fieldsF11 || 0
    },
    {
      key: 'facilitiesCount',
      label: 'Total Campos',
      className: 'hide-on-mobile',
      render: (row) => `${row.facilitiesCount || 0} campos`
    },
    {
      key: 'contactInfo',
      label: 'Contacto',
      className: 'hide-on-mobile',
      render: (row) => row.contactInfo?.name || 'Sin contacto'
    },
    {
      key: 'active',
      label: 'Estado',
      className: 'hide-on-mobile',
      render: (row) => (
        <span className={`status-badge ${row.active ? 'active' : 'inactive'}`}>
          {row.active ? 'Activa' : 'Inactiva'}
        </span>
      )
    }
  ];

  // Acciones completas
  const actions = [
    {
      key: 'view',
      icon: 'visibility',
      label: 'Ver detalles',
      type: 'default',
      onClick: openViewModal
    },
    {
      key: 'edit',
      icon: 'edit',
      label: 'Editar',
      type: 'primary',
      onClick: openEditModal
    },
    {
      key: 'toggle',
      icon: (row) => row.active ? 'visibility_off' : 'visibility',
      label: (row) => row.active ? 'Desactivar' : 'Activar',
      type: (row) => row.active ? 'warning' : 'success',
      onClick: handleToggleActive
    },
    {
      key: 'delete',
      icon: 'delete',
      label: 'Eliminar',
      type: 'danger',
      onClick: openDeleteModal
    }
  ];

  // Botón de acción principal
  const headerActions = (
    <button className="btn btn-primary" onClick={openCreateModal}>
      <span className="material-icons">add</span>
      Añadir Sede
    </button>
  );

  return (
    <AdminCRUDLayout
      title="Gestión de Sedes"
      subtitle="Administra las sedes de la plataforma"
      headerActions={headerActions}
      loading={loading}
      error={error}
      onErrorDismiss={clearError}
    >
      <div className="venue-table">
        <UnifiedDataTable
          columns={columns}
          data={venues}
          actions={actions}
          searchable={true}
          pagination={true}
          onRowClick={openViewModal}
          emptyMessage="No hay sedes disponibles"
        />
      </div>

      {/* Configuración de campos para el formulario */}
      {(() => {
        const formFields = [
          {
            key: 'name',
            label: 'Nombre de la sede',
            type: 'text',
            required: true,
            placeholder: 'Ej: Campo Municipal Norte'
          },
          {
            key: 'direccion',
            label: 'Dirección',
            type: 'text',
            required: true,
            placeholder: 'Calle, número'
          },
          {
            key: 'poblacion',
            label: 'Población',
            type: 'text',
            required: true,
            placeholder: 'Ciudad'
          },
          {
            key: 'ubicacion',
            label: 'Ubicación/Descripción',
            type: 'textarea',
            placeholder: 'Descripción de la ubicación de la sede',
            rows: 3
          },
          {
            key: 'fieldsF8',
            label: 'Número de campos F8',
            type: 'number',
            min: 0,
            placeholder: '2'
          },
          {
            key: 'fieldsF11',
            label: 'Número de campos F11',
            type: 'number',
            min: 0,
            placeholder: '1'
          },
          {
            key: 'contacto',
            label: 'Contacto',
            type: 'text',
            placeholder: 'Responsable de la sede'
          },
          {
            key: 'latitud',
            label: 'Latitud',
            type: 'number',
            step: 0.00000001,
            min: -90,
            max: 90,
            placeholder: '38.0829'
          },
          {
            key: 'longitud',
            label: 'Longitud',
            type: 'number',
            step: 0.00000001,
            min: -180,
            max: 180,
            placeholder: '-0.7963'
          },
          {
            key: 'active',
            label: 'Sede activa',
            type: 'checkbox'
          }
        ];

        return (
          <>
            {/* Modal de creación */}
            <StandardModal
              isOpen={showCreateModal}
              onClose={closeAllModals}
              title="Crear Sede"
              type="create"
              size="large"
              footer={
                <>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={closeAllModals}
                    disabled={formLoading}
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    form="venue-form"
                    className="btn btn-primary"
                    disabled={formLoading}
                  >
                    {formLoading ? 'Creando...' : 'Crear Sede'}
                  </button>
                </>
              }
            >
              <StandardForm
                id="venue-form"
                fields={formFields}
                data={formData}
                onChange={handleFormChange}
                onSubmit={handleCreate}
                errors={formErrors}
                loading={formLoading}
                submitLabel=""
                cancelLabel=""
                onCancel={null}
              />
            </StandardModal>

            {/* Modal de edición */}
            <StandardModal
              isOpen={showEditModal}
              onClose={closeAllModals}
              title="Editar Sede"
              type="edit"
              size="large"
              footer={
                <>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={closeAllModals}
                    disabled={formLoading}
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    form="venue-edit-form"
                    className="btn btn-primary"
                    disabled={formLoading}
                  >
                    {formLoading ? 'Guardando...' : 'Guardar Cambios'}
                  </button>
                </>
              }
            >
              <StandardForm
                id="venue-edit-form"
                fields={formFields}
                data={formData}
                onChange={handleFormChange}
                onSubmit={handleUpdate}
                errors={formErrors}
                loading={formLoading}
                submitLabel=""
                cancelLabel=""
                onCancel={null}
              />
            </StandardModal>

            {/* Modal de visualización */}
            <StandardModal
              isOpen={showViewModal}
              onClose={closeAllModals}
              title="Detalles de la Sede"
              type="view"
              size="large"
              footer={
                <>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={closeAllModals}
                  >
                    Cerrar
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={() => {
                      closeAllModals();
                      setTimeout(() => openEditModal(currentItem), 100);
                    }}
                  >
                    Editar Sede
                  </button>
                </>
              }
            >
              {currentItem && (
                <div className="venue-details">
                  <div className="detail-section">
                    <h4>Información General</h4>
                    <div className="detail-grid">
                      <div className="detail-item">
                        <label>Nombre:</label>
                        <span>{currentItem.name}</span>
                      </div>
                      <div className="detail-item">
                        <label>Dirección:</label>
                        <span>{currentItem.address?.street || 'Sin dirección'}</span>
                      </div>
                      <div className="detail-item">
                        <label>Población:</label>
                        <span>{currentItem.address?.city || 'Sin población'}</span>
                      </div>
                      <div className="detail-item">
                        <label>Ubicación/Descripción:</label>
                        <span>{currentItem.description || 'Sin descripción'}</span>
                      </div>
                      <div className="detail-item">
                        <label>Estado:</label>
                        <span className={`status-badge ${currentItem.active ? 'active' : 'inactive'}`}>
                          {currentItem.active ? 'Activa' : 'Inactiva'}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="detail-section">
                    <h4>Campos Disponibles</h4>
                    <div className="detail-grid">
                      <div className="detail-item">
                        <label>Campos F8:</label>
                        <span className="field-count">{currentItem.fieldsF8 || 0} campos</span>
                      </div>
                      <div className="detail-item">
                        <label>Campos F11:</label>
                        <span className="field-count">{currentItem.fieldsF11 || 0} campos</span>
                      </div>
                      <div className="detail-item">
                        <label>Total de campos:</label>
                        <span className="field-count total">{currentItem.facilitiesCount || 0} campos</span>
                      </div>
                      <div className="detail-item">
                        <label>Contacto:</label>
                        <span>{currentItem.contactInfo?.name || 'Sin contacto'}</span>
                      </div>
                    </div>
                  </div>

                  {(currentItem.coordinates?.lat || currentItem.coordinates?.lng) && (
                    <div className="detail-section">
                      <h4>Ubicación Geográfica</h4>
                      <div className="detail-grid">
                        <div className="detail-item">
                          <label>Latitud:</label>
                          <span>{currentItem.coordinates?.lat || 'No especificada'}</span>
                        </div>
                        <div className="detail-item">
                          <label>Longitud:</label>
                          <span>{currentItem.coordinates?.lng || 'No especificada'}</span>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* ── Instalaciones / Campos ──────────────────────────── */}
                  <div className="detail-section campos-section">
                    <div className="campos-section-header">
                      <h4>Instalaciones de la sede</h4>
                      {campoFormMode === null && (
                        <button
                          className="btn btn-sm btn-primary campos-add-btn"
                          onClick={openAddCampoForm}
                        >
                          <span className="material-icons" style={{ fontSize: 16 }}>add</span>
                          Añadir instalación
                        </button>
                      )}
                    </div>

                    {/* Formulario inline de campo */}
                    {campoFormMode !== null && (
                      <div className="campo-form">
                        <div className="campo-form-title">
                          {campoFormMode === 'add' ? 'Nueva instalación' : `Editando: ${editingCampo?.nombre}`}
                        </div>
                        <div className="campo-form-fields">
                          <div className="campo-form-row campo-form-full">
                            <label>Nombre *</label>
                            <input
                              type="text"
                              placeholder="Ej: Polideportivo Norte"
                              value={campoFormData.nombre || ''}
                              onChange={e => setCampoFormData(p => ({ ...p, nombre: e.target.value }))}
                            />
                          </div>
                          <div className="campo-form-row campo-form-full">
                            <label>Dirección</label>
                            <input
                              type="text"
                              placeholder="Calle y número"
                              value={campoFormData.direccion || ''}
                              onChange={e => setCampoFormData(p => ({ ...p, direccion: e.target.value }))}
                            />
                          </div>
                          <div className="campo-form-row">
                            <label>Subcampos F8</label>
                            <input
                              type="number"
                              min="0"
                              value={campoFormData.num_campos_f8 ?? 0}
                              onChange={e => setCampoFormData(p => ({ ...p, num_campos_f8: parseInt(e.target.value) || 0 }))}
                            />
                          </div>
                          <div className="campo-form-row">
                            <label>Subcampos F11</label>
                            <input
                              type="number"
                              min="0"
                              value={campoFormData.num_campos_f11 ?? 0}
                              onChange={e => setCampoFormData(p => ({ ...p, num_campos_f11: parseInt(e.target.value) || 0 }))}
                            />
                          </div>
                        </div>
                        {campoFormError && (
                          <p className="campo-form-error">{campoFormError}</p>
                        )}
                        <div className="campo-form-actions">
                          <button
                            className="btn btn-sm btn-primary"
                            onClick={handleCampoSave}
                            disabled={campoFormLoading}
                          >
                            {campoFormLoading ? 'Guardando...' : 'Guardar'}
                          </button>
                          <button
                            className="btn btn-sm btn-secondary"
                            onClick={cancelCampoForm}
                            disabled={campoFormLoading}
                          >
                            Cancelar
                          </button>
                        </div>
                      </div>
                    )}

                    {/* Lista de campos */}
                    {camposLoading ? (
                      <p className="campos-empty">Cargando instalaciones...</p>
                    ) : campos.length === 0 ? (
                      <p className="campos-empty">
                        Sin instalaciones definidas — los partidos se asignan a la sede directamente.
                      </p>
                    ) : (
                      <div className="campo-list">
                        {campos.map(campo => (
                          <div
                            key={campo.id}
                            className={`campo-row${!campo.activo ? ' campo-row--inactive' : ''}`}
                          >
                            <div className="campo-row-info">
                              <span className="campo-nombre">{campo.nombre}</span>
                              {campo.direccion && (
                                <span className="campo-dir">{campo.direccion}</span>
                              )}
                              <span className="campo-tags">
                                {campo.num_campos_f8 > 0 && (
                                  <span className="campo-tag">F8: {campo.num_campos_f8}</span>
                                )}
                                {campo.num_campos_f11 > 0 && (
                                  <span className="campo-tag">F11: {campo.num_campos_f11}</span>
                                )}
                              </span>
                            </div>
                            <div className="campo-row-actions">
                              <span className={`status-badge ${campo.activo ? 'active' : 'inactive'}`}>
                                {campo.activo ? 'Activo' : 'Inactivo'}
                              </span>
                              <button
                                className="campo-btn-icon"
                                title="Editar"
                                onClick={() => openEditCampoForm(campo)}
                              >
                                <span className="material-icons">edit</span>
                              </button>
                              <button
                                className="campo-btn-icon"
                                title={campo.activo ? 'Desactivar' : 'Activar'}
                                onClick={() => handleCampoToggleActivo(campo)}
                              >
                                <span className="material-icons">
                                  {campo.activo ? 'visibility_off' : 'visibility'}
                                </span>
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  {/* ─────────────────────────────────────────────────────── */}
                </div>
              )}
            </StandardModal>

            {/* Modal de eliminación */}
            <StandardModal
              isOpen={showDeleteModal}
              onClose={closeAllModals}
              title="Confirmar Eliminación"
              type="delete"
              size="small"
              footer={
                <>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={closeAllModals}
                    disabled={formLoading}
                  >
                    Cancelar
                  </button>
                  <button
                    type="button"
                    className="btn btn-danger"
                    onClick={handleDelete}
                    disabled={formLoading}
                  >
                    {formLoading ? 'Eliminando...' : 'Eliminar'}
                  </button>
                </>
              }
            >
              {currentItem && (
                <div className="delete-confirmation">
                  <div className="warning-icon">
                    <span className="material-icons">warning</span>
                  </div>
                  <p>
                    ¿Estás seguro de que deseas eliminar la sede{' '}
                    <strong>{currentItem.name}</strong>?
                  </p>
                  <p className="warning-text">
                    Esta acción no se puede deshacer y puede afectar a partidos programados.
                  </p>
                </div>
              )}
            </StandardModal>
          </>
        );
      })()}
    </AdminCRUDLayout>
  );
};

export default VenueManagementUnified;
